import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/icons';
import { AsyncPanel, FailureNotice, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  MAPPED_ENTITY_TYPES,
  type CreateMappingRequest,
  type IntegrationMapping,
  type IntegrationMappingHistoryEntry,
  type IntegrationProbeResult,
  type IntegrationRegistration,
  type MappedEntityType,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-09 health and PA-10 mappings, for one registration.
 *
 * MAPPING IS BY IDENTIFIER, AND THERE IS NO NAME-MATCHING OPTION ANYWHERE ON THIS
 * SCREEN. Matching "Form 4B" to "Form 4 B" appears to work until a class is
 * renamed, at which point the mapping silently attaches to a different record and
 * marks are posted against the wrong students — with nothing failing. So both
 * sides are ids, and `externalIdKind` is mandatory because most systems issue
 * several identifiers per record and a mapping built on the mutable one is the
 * same trap wearing a different hat.
 *
 * CONFLICTS ARE SURFACED, NOT RESOLVED SILENTLY. An internal or external record
 * that is already actively mapped produces a 409, and the only way past it is to
 * supply a supersede reason — an accidental remap is far more common than an
 * intentional one, so the intentional one has to be stated.
 */

export function IntegrationDetailPage() {
  const { integrationId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.integrations.get(integrationId), [integrationId]);
  const { state, reload, replace } = useAsyncData<IntegrationRegistration>(
    `integration:${integrationId}`,
    fetcher,
    integrationId.length > 0,
  );

  const [probing, setProbing] = useState(false);
  const [probeError, setProbeError] = useState<ApiError | null>(null);
  const [probeOutcome, setProbeOutcome] = useState<IntegrationProbeResult | null>(null);
  const [stateRecorded, setStateRecorded] = useState(true);
  const [disabling, setDisabling] = useState(false);

  /**
   * The probe runs server-side and cannot run anywhere else: the credential lives
   * in a vault the browser has no access to, which is the whole point of storing a
   * reference. So a failure here is reported as a failure of the probe, never as a
   * verdict on the integration — "we could not check" and "it is broken" call for
   * different actions.
   */
  const testConnectivity = async () => {
    setProbing(true);
    setProbeError(null);
    setProbeOutcome(null);
    try {
      // Three things come back, not one. The recorded STATE and the probe OUTCOME
      // are different facts: a timeout and an unreachable host are both non-healthy
      // to the platform, but one means look at the connector and the other means
      // look at the address. Reading only the registration threw this response
      // away — and, before that, threw a parse error that reported a working probe
      // as unavailable.
      const result = await platformApi.integrations.testConnectivity(integrationId);
      replace(result.registration);
      setProbeOutcome(result.outcome);
      setStateRecorded(result.stateRecorded);
    } catch (cause) {
      setProbeError(
        cause instanceof ApiError ? cause : new ApiError((cause as Error).message, 0, integrationId),
      );
    } finally {
      setProbing(false);
    }
  };

  const disable = async (reason: string) => {
    try {
      replace(
        await platformApi.integrations.setState(integrationId, { state: 'DISABLED', lastError: reason }),
      );
      setDisabling(false);
    } catch {
      // The dialog surfaces its own failure; leaving it open keeps the reason.
    }
  };

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('platform.integrations.breadcrumbLabel')}>
        <Link to="/admin/integrations">{t('platform.integrations.title')}</Link>
        <Icon name="chevronRight" size={13} />
        <span>{state.status === 'ready' ? state.data.displayName : integrationId}</span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(integration) => (
          <>
            <header className={styles.caseHeader}>
              <div>
                <h1 className={styles.pageTitle}>{integration.displayName}</h1>
                <p className={styles.pageLead}>
                  {t(`platform.integrations.connector.${integration.connectorType}`, {
                    defaultValue: integration.connectorType,
                  })}
                  {` · ${integration.code}`}
                </p>
              </div>
              <div className={styles.caseHeaderAside}>
                <StateBadge kind="integration" value={integration.state} />
              </div>
            </header>

            <Panel
              title={t('platform.integrations.healthTitle')}
              icon="pulse"
              actions={
                <button type="button" className={styles.primaryBtnSm} onClick={testConnectivity} disabled={probing}>
                  <Icon name="refresh" size={15} />
                  {probing ? t('platform.integrations.testing') : t('platform.integrations.test')}
                </button>
              }
            >
              <p className={styles.dataNotice}>
                <Icon name="info" size={15} aria-hidden="true" />
                {t('platform.integrations.stateNotice')}
              </p>

              <dl className={styles.detailGrid}>
                <dt>{t('platform.integrations.colState')}</dt>
                <dd>
                  <StateBadge kind="integration" value={integration.state} size="sm" />
                  <span className={styles.fieldHint}>
                    {t(`platform.state.integrationMeaning.${integration.state}`, { defaultValue: '' })}
                  </span>
                </dd>
                <dt>{t('platform.integrations.lastSuccessfulSync')}</dt>
                <dd>
                  {integration.lastSyncAt
                    ? `${formatDateTime(integration.lastSyncAt, locale)} (${formatRelative(integration.lastSyncAt, locale)})`
                    : t('platform.integrations.neverSynced')}
                  {integration.lastSyncStatus ? ` · ${integration.lastSyncStatus}` : ''}
                </dd>
                <dt>{t('platform.integrations.lastHealthCheck')}</dt>
                <dd>{formatDateTime(integration.lastHealthCheckAt, locale)}</dd>
                <dt>{t('platform.integrations.fieldExternalTenant')}</dt>
                <dd>{integration.externalTenantId || '—'}</dd>
                <dt>{t('platform.integrations.fieldBaseUrl')}</dt>
                <dd>{integration.baseUrl || '—'}</dd>
                <dt>{t('platform.integrations.fieldVaultRef')}</dt>
                <dd>
                  {/* A pointer, not a secret. It is shown in full because being able
                      to read which vault path is in use is how a rotation is
                      verified without anybody fetching the value. */}
                  <code className={styles.code}>{integration.vaultSecretRef || t('platform.integrations.noCredential')}</code>
                  {integration.vaultSecretVersion && (
                    <span className={styles.fieldHint}>
                      {t('platform.integrations.vaultVersion', { version: integration.vaultSecretVersion })}
                    </span>
                  )}
                </dd>
              </dl>

              {integration.lastError && (
                <p className={styles.rowDetailError} role="status">
                  {integration.lastError}
                </p>
              )}

              {probeError && <FailureNotice error={probeError} onRetry={testConnectivity} />}

              {probeOutcome && (
                <div className={styles.warningNotice} role="status">
                  <Icon name="pulse" size={16} aria-hidden="true" />
                  <div>
                    <p className={styles.warningTitle}>
                      {t(`platform.integrations.probe.${probeOutcome.kind}`, {
                        defaultValue: probeOutcome.kind,
                      })}
                    </p>
                    <p className={styles.warningBody}>{probeOutcome.detail}</p>
                    <p className={styles.rowMetaLine}>
                      {probeOutcome.responseTimeMs !== null
                        ? t('platform.integrations.probeLatency', { ms: probeOutcome.responseTimeMs })
                        : t('platform.integrations.probeNoRoundTrip')}
                      {probeOutcome.httpStatus !== null
                        ? ` · ${t('platform.integrations.probeStatus', { status: probeOutcome.httpStatus })}`
                        : ''}
                    </p>
                    {/*
                      NOT_PROBEABLE is not a failure — no base URL is recorded, so
                      there was nothing to contact. Rendering it red would put a
                      fault against an integration whose only problem is that
                      nobody has finished configuring it.
                    */}
                    {!stateRecorded && (
                      <p className={styles.rowDetail}>{t('platform.integrations.probeNotRecorded')}</p>
                    )}
                  </div>
                </div>
              )}

              {integration.state !== 'DISABLED' && (
                <div className={styles.transitionRow}>
                  <button type="button" className={styles.dangerBtn} onClick={() => setDisabling(true)}>
                    {t('platform.integrations.disable')}
                  </button>
                </div>
              )}
            </Panel>

            <MappingsPanel integrationId={integrationId} locale={locale} />
            <MappingHistoryPanel integrationId={integrationId} locale={locale} />

            {disabling && (
              <ConfirmActionDialog
                open
                title={t('platform.integrations.disableTitle', { name: integration.displayName })}
                intro={t('platform.integrations.disableIntro', { name: integration.displayName })}
                tone="critical"
                impactStatus="ready"
                impact={
                  <ul className={styles.impactList}>
                    <li>{t('platform.integrations.disableImpactStops')}</li>
                    <li>{t('platform.integrations.disableImpactMappings')}</li>
                    <li>{t('platform.integrations.disableImpactProbe')}</li>
                  </ul>
                }
                confirmPhrase={integration.code}
                confirmLabel={t('platform.integrations.disable')}
                submitting={false}
                onCancel={() => setDisabling(false)}
                onConfirm={disable}
              />
            )}
          </>
        )}
      </AsyncPanel>
    </div>
  );
}

/* ─── Mappings ─── */

function MappingsPanel({ integrationId, locale }: { integrationId: string; locale: string }) {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<MappedEntityType | 'ALL'>('ALL');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [retiring, setRetiring] = useState<IntegrationMapping | null>(null);

  const fetcher = useCallback(
    () =>
      platformApi.integrations.mappings(
        integrationId,
        entityType === 'ALL' ? undefined : entityType,
        includeInactive,
      ),
    [integrationId, entityType, includeInactive],
  );
  const { state, reload } = useAsyncData<IntegrationMapping[]>(
    `mappings:${integrationId}:${entityType}:${includeInactive}`,
    fetcher,
    integrationId.length > 0,
  );

  return (
    <Panel
      title={t('platform.mappings.title')}
      icon="switch"
      actions={
        <button type="button" className={styles.primaryBtnSm} onClick={() => setCreating(true)}>
          <Icon name="plus" size={15} />
          {t('platform.mappings.create')}
        </button>
      }
    >
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.mappings.identifierNotice')}
      </p>

      <div className={styles.filterBar}>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="mapping-entity">
            {t('platform.mappings.filterEntity')}
          </label>
          <Select
            id="mapping-entity"
            value={entityType}
            onChange={(value) => setEntityType(value as MappedEntityType | 'ALL')}
            options={[
              { value: 'ALL', label: t('platform.mappings.filterEntityAll') },
              ...MAPPED_ENTITY_TYPES.map((type) => ({
                value: type,
                label: t(`platform.mappings.entity.${type}`, { defaultValue: type }),
              })),
            ]}
          />
        </div>
        <Checkbox
          label={t('platform.mappings.includeInactive')}
          checked={includeInactive}
          onChange={(event) => setIncludeInactive(event.target.checked)}
        />
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.mappings.emptyTitle')}
        emptyBody={t('platform.mappings.emptyBody')}
      >
        {(data) => (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className={styles.tableCaption}>{t('platform.mappings.tableCaption', { n: data.length })}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('platform.mappings.colEntity')}</th>
                  <th scope="col">{t('platform.mappings.colInternal')}</th>
                  <th scope="col">{t('platform.mappings.colExternal')}</th>
                  <th scope="col">{t('platform.mappings.colKind')}</th>
                  <th scope="col">{t('platform.mappings.colStatus')}</th>
                  <th scope="col">
                    <span className={styles.srOnlyText}>{t('platform.mappings.colActions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((mapping) => (
                  <tr key={mapping.id}>
                    <th scope="row" className={styles.tableRowHeader}>
                      {t(`platform.mappings.entity.${mapping.entityType}`, { defaultValue: mapping.entityType })}
                    </th>
                    <td>
                      <code className={styles.code}>{mapping.internalId}</code>
                    </td>
                    <td>
                      <code className={styles.code}>{mapping.externalId}</code>
                    </td>
                    <td className={styles.mutedCell}>{mapping.externalIdKind}</td>
                    <td>
                      <span className={mapping.isActive ? styles.okTag : styles.mutedTag}>
                        {mapping.isActive ? t('platform.mappings.active') : t('platform.mappings.retired')}
                      </span>
                      {mapping.supersededById && (
                        <span className={styles.tableSub}>{t('platform.mappings.superseded')}</span>
                      )}
                      {mapping.deactivatedAt && (
                        <span className={styles.tableSub}>{formatDateTime(mapping.deactivatedAt, locale)}</span>
                      )}
                    </td>
                    <td>
                      {mapping.isActive && (
                        <button type="button" className={styles.linkBtnDanger} onClick={() => setRetiring(mapping)}>
                          {t('platform.mappings.retire')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>

      <CreateMappingDialog
        open={creating}
        integrationId={integrationId}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          reload();
        }}
      />

      <RetireMappingDialog
        mapping={retiring}
        integrationId={integrationId}
        onClose={() => setRetiring(null)}
        onRetired={() => {
          setRetiring(null);
          reload();
        }}
      />
    </Panel>
  );
}

function CreateMappingDialog({
  open,
  integrationId,
  onClose,
  onCreated,
}: {
  open: boolean;
  integrationId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateMappingRequest>(emptyMapping());
  const [conflict, setConflict] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyMapping());
      setConflict(false);
      setError(null);
    }
  }, [open]);

  const supersedeValid = !conflict || (form.supersedeReason ?? '').trim().length >= 3;
  const valid =
    form.internalId.trim().length > 0 &&
    form.externalId.trim().length > 0 &&
    form.externalIdKind.trim().length > 0 &&
    supersedeValid;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.integrations.createMapping(integrationId, {
        entityType: form.entityType,
        internalId: form.internalId.trim(),
        externalId: form.externalId.trim(),
        externalIdKind: form.externalIdKind.trim(),
        supersedeReason: conflict ? form.supersedeReason?.trim() : undefined,
      });
      onCreated();
    } catch (cause) {
      // A 409 means one side is already mapped. Rather than a bare error, the form
      // switches into the supersede path: replacing a mapping is legitimate, and
      // what the server is insisting on is that it be stated rather than assumed.
      if (cause instanceof ApiError && cause.status === 409) {
        setConflict(true);
        setError(t('platform.mappings.conflictDetected'));
      } else {
        setError((cause as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.mappings.createTitle')}
      description={t('platform.mappings.createLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.mappings.colEntity')}</span>
          <Select
            value={form.entityType}
            onChange={(value) => setForm({ ...form, entityType: value as MappedEntityType })}
            options={MAPPED_ENTITY_TYPES.map((type) => ({
              value: type,
              label: t(`platform.mappings.entity.${type}`, { defaultValue: type }),
            }))}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.mappings.colInternal')}</span>
          <input
            className={styles.input}
            value={form.internalId}
            required
            autoComplete="off"
            onChange={(event) => setForm({ ...form, internalId: event.target.value })}
          />
          <span className={styles.fieldHint}>{t('platform.mappings.internalHint')}</span>
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.mappings.colExternal')}</span>
            <input
              className={styles.input}
              value={form.externalId}
              required
              autoComplete="off"
              onChange={(event) => setForm({ ...form, externalId: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.mappings.colKind')}</span>
            <input
              className={styles.input}
              value={form.externalIdKind}
              required
              placeholder="sis_student_number"
              autoComplete="off"
              onChange={(event) => setForm({ ...form, externalIdKind: event.target.value })}
            />
            <span className={styles.fieldHint}>{t('platform.mappings.kindHint')}</span>
          </label>
        </div>

        {conflict && (
          <>
            <div className={styles.warningNotice} role="alert">
              <Icon name="warning" size={16} aria-hidden="true" />
              <div>
                <p className={styles.warningTitle}>{t('platform.mappings.conflictTitle')}</p>
                <p className={styles.warningBody}>{t('platform.mappings.conflictBody')}</p>
              </div>
            </div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.mappings.supersedeReason')}</span>
              <textarea
                className={styles.textarea}
                rows={3}
                value={form.supersedeReason ?? ''}
                required
                onChange={(event) => setForm({ ...form, supersedeReason: event.target.value })}
              />
              <span className={styles.fieldHint}>{t('platform.mappings.supersedeHint')}</span>
            </label>
          </>
        )}

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting
              ? t('platform.confirm.submitting')
              : conflict
                ? t('platform.mappings.supersede')
                : t('platform.mappings.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyMapping(): CreateMappingRequest {
  return { entityType: 'STUDENT', internalId: '', externalId: '', externalIdKind: '', supersedeReason: '' };
}

function RetireMappingDialog({
  mapping,
  integrationId,
  onClose,
  onRetired,
}: {
  mapping: IntegrationMapping | null;
  integrationId: string;
  onClose: () => void;
  onRetired: () => void;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!mapping) return null;

  const run = async (reason: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.integrations.deactivateMapping(integrationId, mapping.id, reason);
      onRetired();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmActionDialog
      open
      title={t('platform.mappings.retireTitle')}
      intro={t('platform.mappings.retireIntro', {
        internal: mapping.internalId,
        external: mapping.externalId,
      })}
      tone="warning"
      impactStatus="ready"
      impact={
        <ul className={styles.impactList}>
          <li>{t('platform.mappings.retireImpactSync')}</li>
          <li>{t('platform.mappings.retireImpactKept')}</li>
        </ul>
      }
      confirmLabel={t('platform.mappings.retire')}
      submitting={submitting}
      error={error}
      onCancel={onClose}
      onConfirm={run}
    />
  );
}

/* ─── History ─── */

function MappingHistoryPanel({ integrationId, locale }: { integrationId: string; locale: string }) {
  const { t } = useTranslation();
  const fetcher = useCallback(() => platformApi.integrations.mappingHistory(integrationId), [integrationId]);
  const { state, reload } = useAsyncData<IntegrationMappingHistoryEntry[]>(
    `mapping-history:${integrationId}`,
    fetcher,
    integrationId.length > 0,
  );

  return (
    <Panel title={t('platform.mappings.historyTitle')} icon="history">
      <p className={styles.fieldHint}>{t('platform.mappings.historyLead')}</p>
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.mappings.historyEmpty')}
      >
        {(data) => (
          <ol className={styles.timeline}>
            {data.map((entry) => (
              <li key={entry.id} className={styles.timelineItem}>
                <span className={styles.timelineDot} aria-hidden="true" />
                <div>
                  <p className={styles.rowTitle}>
                    {t(`platform.mappings.historyAction.${entry.action}`, { defaultValue: entry.action ?? '—' })}
                    {entry.entityType
                      ? ` · ${t(`platform.mappings.entity.${entry.entityType}`, { defaultValue: entry.entityType })}`
                      : ''}
                  </p>
                  <p className={styles.rowMetaLine}>
                    {formatDateTime(entry.occurredAt ?? entry.createdAt, locale)}
                    {entry.actor ? ` · ${entry.actor}` : ''}
                    {entry.internalId ? ` · ${entry.internalId}` : ''}
                    {entry.externalId ? ` → ${entry.externalId}` : ''}
                  </p>
                  {entry.reason && <p className={styles.rowDetail}>{entry.reason}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </AsyncPanel>
    </Panel>
  );
}
